import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { ZUid } from "src/types/zUid";
import { Sheet2Store } from "src/components/Sheet2/Sheet2Store";
import { Cell2 } from "src/components/Sheet2/Cell2";
import { PropsCell } from "src/components/Sheet2/PropsCell2";
import {
  changeMc2RowPositions,
  createMc2Rows,
  deleteMc2Rows,
  importMc2Rows,
  loadMc2EntitySizes,
  loadMc2Table,
  mapMc2Sizes,
  mc2Scale,
  updateMc2BaseSize,
  updateMc2Row,
} from "./apiMc2Entity";
import { Mc2Column } from "./Mc2Table/Mc2Column";
import { buildMc2Columns, sizeColumnKey } from "./Mc2Table/buildMc2Columns";
import {
  emptyTableResponse,
  ZMc2SizeRec,
  ZMc2TableResponse,
  ZMc2TableRow,
} from "./Mc2EntityTypes";
import {
  cvtMc2PointRowToTableRow,
  Mc2PointRow,
} from "./Mc2PointSelect/Mc2PointRow";
import { ZMc2, ZMc2Settings } from "../../Mc2Types";
import { loadMc2Entity, updateMc2 } from "../../apiMc2";
import { Mc2SizeMapData } from "./Mc2EntitySizeLine/Mc2SizeMapData";
import { hasMc2Permission, Mc2Permissions } from "../../mc2Permissions";
import { loadSizeLineAttrNameId } from "../loadSizeLineAttrNameId";

/* eslint no-plusplus: "off" */

export type Mc2EntityTaskType =
  | "baseSize"
  | "createEmpty"
  | "createFromLib"
  | "delete"
  | "import"
  | "mapSize"
  | "pointsOrder"
  | "scaling"
  | "updateEntity"
  | null;

export type Mc2EntityMode =
  | "baseSize"
  | "delete"
  | "import"
  | "mapSizes"
  | "pointsLib"
  | "pointsOrder"
  | null;

export class Mc2EntityStore {
  constructor(
    public mcEntity: ZMc2,
    public readonly settings: ZMc2Settings,
    public readonly onEntityUpdate: () => void,
  ) {
    this.curComment = mcEntity.comment ?? "";
    makeAutoObservable(this);
  }

  async init() {
    try {
      this.sheetStore = new Sheet2Store();
      this.clearSelected();
      this.lock();
      this.setTable(emptyTableResponse);
      await this.reload();
      this.setSizeLineAttrNameId(
        await loadSizeLineAttrNameId(this.settings.objSizeLineId),
      );
    } finally {
      this.unlock();
    }
  }

  sizeLineAttrNameId = "";

  setSizeLineAttrNameId(id: string) {
    this.sizeLineAttrNameId = id;
  }

  get readOnly(): boolean {
    return !hasMc2Permission(this.mcEntity, Mc2Permissions.update);
  }

  async reload() {
    const res = await loadMc2Table(this.mcEntity.id);
    this.setTable(res);
  }

  async update<TRes = void>(
    taskType: Mc2EntityTaskType,
    task: () => Promise<TRes>,
  ) {
    const { taskType: oldTaskType } = this;
    try {
      this.setTaskType(taskType);
      this.lock();
      const res = await task();
      await this.reload();
      return res;
    } finally {
      this.unlock();
      this.setTaskType(oldTaskType);
    }
  }

  table: ZMc2TableResponse = emptyTableResponse;

  setTable(data: ZMc2TableResponse) {
    this.table = data;
  }

  lockCounter = 0;

  lock() {
    this.lockCounter++;
  }

  unlock() {
    this.lockCounter--;
  }

  get buzy(): boolean {
    return this.lockCounter !== 0;
  }

  isDisabled(taskType: Mc2EntityTaskType): boolean {
    return (
      (this.buzy && this.taskType !== taskType) || !!this.getReason(taskType)
    );
  }

  getReason(taskType: Mc2EntityTaskType): string | undefined {
    const { selected } = this;
    switch (taskType) {
      case "delete":
      case "scaling":
        if (selected.size === 0)
          return "Необходимо выбрать хотя бы одну строку";
        break;
      default:
        break;
    }
    return undefined;
  }

  taskType: Mc2EntityTaskType = null;

  setTaskType(tt: Mc2EntityTaskType) {
    this.taskType = tt;
  }

  mode: Mc2EntityMode = null;

  setMode(newMode: Mc2EntityMode) {
    this.mode = newMode;
  }

  baseSizeOptions: string[] = [];

  setBaseSizeOptions(value: string[]) {
    this.baseSizeOptions = value;
  }

  async startBaseSizeChange() {
    const { baseSize } = this.mcEntity;
    const { taskType: oldTaskType } = this;
    this.setTaskType("baseSize");
    this.lock();
    try {
      this.setMode("baseSize");
      const sizes = await loadMc2EntitySizes(this.mcEntity.id);
      if (baseSize && !sizes.includes(baseSize)) {
        this.setBaseSizeOptions([baseSize, ...sizes]);
        return;
      }
      this.setBaseSizeOptions(sizes);
    } finally {
      this.unlock();
      this.setTaskType(oldTaskType);
    }
  }

  async changeBaseSize(baseSize: string) {
    const mcEntityId = this.mcEntity.id;
    await this.update("baseSize", async () => {
      await updateMc2BaseSize(mcEntityId, baseSize);
      this.setEntity(await loadMc2Entity(mcEntityId));
      this.onEntityUpdate();
      this.setMode(null);
    });
  }

  selected = new Set<ZUid>();

  select(id: ZUid, enable: boolean) {
    if (enable) {
      this.selected.add(id);
    } else {
      this.selected.delete(id);
    }
  }

  toggleTotalSelection() {
    const { table, selected } = this;
    if (selected.size === table.rows.length) {
      selected.clear();
    } else {
      table.rows.forEach(({ id }) => selected.add(id));
    }
  }

  clearSelected() {
    this.selected.clear();
  }

  // Показ изображений
  isImagesVisible = false;

  setImageVisible(value: boolean) {
    this.isImagesVisible = value;
  }

  imgWidth = 250;

  setImgWidth(w: number) {
    this.imgWidth = w;
  }

  imgX0 = 0;

  setImgX0(x0: number) {
    this.imgX0 = x0;
  }

  async getImages(): Promise<string[]> {
    return this.mcEntity.images?.map((id) => String(id)) ?? [];
  }

  async saveImages(images: ZUid[]): Promise<string[]> {
    const mc = await this.updateEntityAsync("images", images);
    return mc.images?.map((id) => String(id)) ?? [];
  }

  get columns(): Mc2Column[] {
    return buildMc2Columns(this);
  }

  // create row

  async createPoint() {
    const { table } = this;
    const row: Omit<ZMc2TableRow, "id"> = cvtMc2PointRowToTableRow(
      this.mcEntity.id,
      table.header,
    );
    await this.update("createEmpty", async () => {
      await createMc2Rows(this.mcEntity.id, [row]);
    });
  }

  async createPoints(points: Mc2PointRow[]) {
    const { table } = this;
    const rows: Omit<ZMc2TableRow, "id">[] = points.map((pt) => {
      const dst = cvtMc2PointRowToTableRow(this.mcEntity.id, table.header, pt);
      return dst;
    });
    await this.update("createEmpty", async () => {
      await createMc2Rows(this.mcEntity.id, rows);
    });
  }

  startCreateFromLib() {
    this.setMode("pointsLib");
  }

  startDeleteRows() {
    if (this.getReason("delete")) return;
    this.setMode("delete");
  }

  async finalDelete() {
    const rowIds = Array.from(this.selected);
    if (rowIds.length === 0) return;
    await this.update("delete", async () => {
      await deleteMc2Rows(rowIds);
      this.setMode(null);
    });
  }

  sheetStore = new Sheet2Store();

  mkCell<Field extends keyof ZMc2TableRow>(
    field: Field,
    row: ZMc2TableRow,
    emptyValue: ZMc2TableRow[Field],
  ): PropsCell {
    const x = this.columns.findIndex(({ key }) => key === field);
    const y = this.table.rows.findIndex(({ id }) => id === row.id);
    const pos = { x, y };
    const makeNewRow = (
      prevRow: ZMc2TableRow,
      value: unknown,
    ): ZMc2TableRow => ({
      ...prevRow,
      [field]: value as ZMc2TableRow[Field],
    });
    return {
      cellKey: `${field}:${row.id}`,
      store: this.sheetStore,
      pos,
      emptyValue,
      value: row[field],
      save: this.mkSave(row.id, makeNewRow),
    };
  }

  mkSizeCell(size: string, row: ZMc2TableRow): PropsCell {
    const colKey = sizeColumnKey(size);
    const x = this.columns.findIndex(({ key }) => key === colKey);
    const y = this.table.rows.findIndex(({ id }) => id === row.id);
    const pos = { x, y };
    const makeNewRow = (
      prevRow: ZMc2TableRow,
      value: unknown,
    ): ZMc2TableRow => {
      const newRow = { ...prevRow };
      const newSize = {
        ...newRow.sizes[size],
        value: typeof value === "number" ? value : 0,
      } as ZMc2SizeRec;
      newRow.sizes[size] = newSize;
      return newRow;
    };
    return {
      cellKey: `${colKey}:${row.id}`,
      store: this.sheetStore,
      pos,
      emptyValue: 0,
      value: row.sizes[size]?.value,
      save: this.mkSave(row.id, makeNewRow),
    };
  }

  mkSave(
    rowId: ZUid,
    makeNewRow: (prevRow: ZMc2TableRow, value: unknown) => ZMc2TableRow,
  ): Cell2["save"] {
    let actualGuard = 0;
    return async (value: unknown) => {
      const row = this.table.rows.find(({ id }) => id === rowId);
      if (!row) throw Error(`Не найдена строка с id=$P{rowId}`);

      const requestRow = makeNewRow(row, value);
      const currentGuard = ++actualGuard;
      const resultRow = await updateMc2Row(requestRow);
      return () => {
        if (currentGuard === actualGuard) {
          this.updateRow(resultRow);
        }
      };
    };
  }

  updateRow(row: ZMc2TableRow) {
    const rowIndex = this.table.rows.findIndex(({ id }) => id === row.id);
    if (rowIndex >= 0) {
      this.table.rows[rowIndex] = row;
    }
  }

  // Изменение базового размера и комментария
  updateEntity<Field extends "comment" | "images">(
    field: Field,
    value: ZMc2[Field],
  ) {
    this.updateEntityAsync(field, value).catch(onError);
  }

  async updateEntityAsync<Field extends "baseSize" | "comment" | "images">(
    field: Field,
    value: ZMc2[Field],
  ) {
    return this.update("updateEntity", async () => {
      const newEntity = this.modifiedEntity(field, value);
      const mc: ZMc2 = await updateMc2(this.mcEntity.id, newEntity);
      this.setEntity(newEntity);
      this.onEntityUpdate();
      return mc;
    });
  }

  setEntity(entiry: ZMc2) {
    this.mcEntity = entiry;
  }

  modifiedEntity<Field extends keyof ZMc2>(
    field: Field,
    value: ZMc2[Field],
  ): ZMc2 {
    return {
      ...this.mcEntity,
      [field]: value,
    };
  }

  curComment: string;

  setCurComment(value: string) {
    this.curComment = value;
  }

  // points order

  async changePointsOrder(rowIds: string[]) {
    await this.update("pointsOrder", async () => {
      await changeMc2RowPositions(rowIds);
      this.sheetStore.postUpdate();
      this.setMode(null);
    });
  }

  async doImport(text: string) {
    await this.update("import", async () => {
      await importMc2Rows(this.mcEntity.id, text);
      this.setMode(null);
    });
  }

  // sizes map
  get initialSizeMapData(): Mc2SizeMapData {
    const { baseSize } = this.mcEntity;
    return {
      srcName: this.mcEntity.sizeLineName ?? "",
      dstName: "",
      dstId: "",
      points: this.table.header.map((nameFrom) => ({
        nameFrom,
        nameTo: "",
        baseSize: baseSize === nameFrom,
      })),
    };
  }

  async doSizesMap(data: Mc2SizeMapData) {
    const mcEntityId = this.mcEntity.id;
    await this.update("mapSize", async () => {
      await mapMc2Sizes(
        {
          mcEntityId,
          sizeLineEntityID: data.dstId,
        },
        data.points,
      );
      this.setEntity(await loadMc2Entity(mcEntityId));
      this.onEntityUpdate();
    });
  }

  async scaling() {
    const rowIds: ZUid[] = Array.from(this.selected);
    if (rowIds.length === 0) return;
    await this.update("scaling", async () => {
      await mc2Scale(rowIds);
    });
    this.clearSelected();
  }
}
